import React, { type ReactNode, type FC } from 'react';
import { Steps as AntSteps, Popconfirm, type StepsProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Link } from '@g2u/ui-kit';
import cx from 'classnames';

import { PopconfirmDescription } from 'components';
import { type WithOptionalDataTest } from 'types';
import styles from './steps.module.scss';

export namespace Steps {
  export type Props = StepsProps & {
    current: number;
    hasSlot: boolean;
    config?: StepDetails[];
    isTraining?: boolean;
  } & WithOptionalDataTest;

  type StepDetails = {
    description: string | ReactNode;
    url: string;
    id: number;
  };

  export const $: FC<Props> = (props) => {
    const { current, config, dataTestId, hasSlot, isTraining, ...antProps } = props;
    const navigate = useNavigate();

    const getProgressTitle = (i: number) => {
      return i < current ? 'Finished' : i > current ? 'Waiting' : 'In Progress';
    };

    const defaultConfig = config ?? [
      {
        description: 'Confirm location & job details',
        url: '/booking/location',
        id: 1,
      },
      {
        description: 'Select technician & time',
        url: '/booking/technician',
        id: 2,
      },
      {
        description: 'Customer & payment details',
        url: '/booking/payment',
        id: 3,
      },
      {
        description: 'Send notifications',
        url: '/booking/confirmation',
        id: 4,
      },
    ];

    return (
      <>
        <AntSteps
          {...antProps}
          initial={0}
          current={current - 1}
          className={styles.steps}
          data-testid={dataTestId}
          items={defaultConfig.map((step, i) => {
            const isCurrent = current === i + 1;
            const isClickable = current > i + 1;

            const itemClassNames = cx(
              styles.steps__item,
              isCurrent && styles.steps__item_current,
              !isClickable && styles.steps__item_disabled,
            );

            return {
              title: (
                <div className={cx(itemClassNames, isTraining && styles.steps__item_training)}>
                  <div>{getProgressTitle(i + 1)}</div>
                </div>
              ),
              description: (
                <div key={i} className={itemClassNames}>
                  {step.description}{' '}
                  {isClickable &&
                    current < 3 && ( // 3 = payment step, the job shouldn't be edited once confirmed (4 step)
                      <Popconfirm
                        title="Alert"
                        onConfirm={() => {
                          navigate(step.url);
                        }}
                        description={<PopconfirmDescription.$ hasSlot={hasSlot} />}
                        okText="Yes"
                      >
                        <div>
                          <Link.$ underline>Edit</Link.$>
                        </div>
                      </Popconfirm>
                    )}
                </div>
              ),
              disabled: !isClickable && !isCurrent,
            };
          })}
        />
      </>
    );
  };
}
